/**
 * Helpers for surfacing validation failures where the user is actually looking.
 *
 * Fields rendered by BaseTextField / BaseAutoSelect tag their wrapper with
 * `data-field-error="true"` while invalid, so the first failure in document
 * order can be found without every form having to report its own field order.
 */

export const INVALID_FIELD_SELECTOR = '[data-field-error="true"]';

/**
 * Scrolls the first invalid field on the page into view and focuses its input.
 * Returns true when something was found to focus.
 */
export const focusFirstInvalidField = ({ behavior = "smooth" } = {}) => {
  // Validation dispatches to redux, so wait a frame for the error props to paint.
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      const wrapper = document.querySelector(INVALID_FIELD_SELECTOR);

      if (!wrapper) {
        resolve(false);
        return;
      }

      wrapper.scrollIntoView({ behavior, block: "center" });

      const control = wrapper.querySelector(
        "input:not([type='hidden']), textarea, select, [contenteditable='true']",
      );
      // preventScroll keeps focus from fighting the smooth scroll above.
      control?.focus?.({ preventScroll: true });

      resolve(true);
    });
  });
};

/**
 * Accepts either the legacy boolean an `isValid()` handle used to return, or the
 * richer `{ valid, errors }` shape, and always yields the latter. Lets forms be
 * migrated one at a time without breaking the step aggregators.
 *
 * null/undefined means "nothing to validate" — the form isn't mounted, e.g. a
 * section hidden for this role — and counts as valid. Treating an absent form
 * as a failure produces a dead end: the step refuses to advance and there is no
 * error to show, because a form that never ran can't report one.
 */
export const normalizeValidationResult = (result) => {
  if (result === null || result === undefined) {
    return { valid: true, errors: [] };
  }

  if (typeof result === "object") {
    return {
      valid: !!result.valid,
      errors: Array.isArray(result.errors) ? result.errors : [],
    };
  }

  return { valid: !!result, errors: [] };
};

/**
 * Combines several `isValid()` results into one. Every child is evaluated —
 * no short-circuiting — so the user sees every problem at once.
 */
export const combineValidationResults = (...results) => {
  const normalized = results.map(normalizeValidationResult);

  return {
    valid: normalized.every((result) => result.valid),
    errors: normalized.flatMap((result) => result.errors),
  };
};

/**
 * Builds the single summary message shown instead of one toast per bad field.
 *
 * Deliberately terse: the offending field is already scrolled to, focused and
 * flagged inline, so the toast only has to say how much is left to do.
 */
export const buildValidationMessage = (errors) => {
  if (!errors.length) return "";
  if (errors.length === 1) return errors[0];

  return `Please complete ${errors.length} required fields`;
};
